import { type ZodIssueLike, formatZodIssues } from '@smart-link/shared';

/**
 * 三个独立运行时。环境切换器只改「数据请求」打哪个 worker:
 *   beta = smart-link-api(本地 D1,全局规则 rules:v1)
 *   prod-dc = smart-link-api-prod(现有 DC,全局规则 rules:prod:v1)
 *   prod-rb = smart-link-api-prod-rb(隔离 RB,全局规则 rules:prod:v1)
 * 鉴权恒定走 beta(Feishu OAuth 配在那);只要两个 prod Worker 的 JWT_SECRET 与 beta 一致,
 * beta 登录拿到的 token 在 prod Worker 也通过校验(requireAdmin 仅验 JWT),
 * 故切环境无需重新登录、prod 侧也不必配 OAuth / Origin。
 */
export const API_ENVS = [
  { id: 'beta', label: 'Beta', base: import.meta.env.VITE_API_BASE ?? '' },
  {
    id: 'prod-dc',
    label: 'DC',
    base: import.meta.env.VITE_API_BASE_PROD ?? 'https://smart-link-api-prod.chanyan.workers.dev',
  },
  {
    id: 'prod-rb',
    label: 'RB',
    base:
      import.meta.env.VITE_API_BASE_PROD_RB ?? 'https://smart-link-api-prod-rb.chanyan.workers.dev',
  },
] as const;

export type ApiEnvId = (typeof API_ENVS)[number]['id'];

const ENV_KEY = 'smart-link.admin-env';
const TOKEN_KEY = 'smart-link.admin-token';

export function getApiEnv(): ApiEnvId {
  const stored = localStorage.getItem(ENV_KEY);
  if (stored === 'prod-rb') return 'prod-rb';
  // Migrate the existing two-state selector without changing its meaning.
  if (stored === 'prod' || stored === 'prod-dc') return 'prod-dc';
  return 'beta';
}

export function setApiEnv(id: ApiEnvId): void {
  localStorage.setItem(ENV_KEY, id);
}

export function isProdApiEnv(id: ApiEnvId = getApiEnv()): boolean {
  return id !== 'beta';
}

/** 当前环境的数据请求基地址(供 api() / uploadImage() 使用)。 */
export function getApiBase(id: ApiEnvId = getApiEnv()): string {
  return API_ENVS.find((e) => e.id === id)?.base ?? API_ENVS[0].base;
}

/** 鉴权恒定走 beta worker(见上)。 */
export const AUTH_BASE = API_ENVS[0].base;

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export interface AdminUser {
  username: string;
  name: string;
  avatar: string;
  email: string;
  provider: 'feishu' | 'password';
}

/** Decode (not verify) the current token's payload for display purposes only. */
export function getUser(): AdminUser | null {
  const token = getToken();
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    // base64url -> bytes -> UTF-8 (atob alone mangles multi-byte names, e.g. Chinese).
    const bin = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const p = JSON.parse(json) as Record<string, unknown>;
    const username = typeof p.sub === 'string' ? p.sub : '';
    if (!username) return null;
    return {
      username,
      name: typeof p.name === 'string' ? p.name : username,
      avatar: typeof p.avatar === 'string' ? p.avatar : '',
      email: typeof p.email === 'string' ? p.email : '',
      provider: p.provider === 'feishu' ? 'feishu' : 'password',
    };
  } catch {
    return null;
  }
}

/** 签发方:token 由这里签出,三个环境共用同一份(见文件顶部)。 */
const TOKEN_ISSUER_ENV: ApiEnvId = 'beta';

export type UnauthorizedAction = { kind: 'signOut' } | { kind: 'keepSession'; message: string };

/**
 * 401 的处置决策(纯函数,副作用见 handleUnauthorized)。
 *
 * 只有签发方(beta)有资格宣布 token 失效。prod 运行时返回 401 更可能是那个
 * Worker 的 JWT_SECRET 与 beta 漂移了 —— 此时清掉共用 token 会把 beta / 另一个
 * prod 的登录态一起毁掉。2026-08-06 就是这样:prod-rb 的 secret 不一致,切到 RB
 * 一次 401,整个后台被登出。
 */
export function unauthorizedAction(env: ApiEnvId): UnauthorizedAction {
  if (env === TOKEN_ISSUER_ENV) return { kind: 'signOut' };
  const label = API_ENVS.find((e) => e.id === env)?.label ?? env;
  return {
    kind: 'keepSession',
    message: `${label} 环境拒绝了当前登录态 (401)：该 Worker 的 JWT_SECRET 可能与 Beta 不一致。登录态已保留，切回 Beta 仍可使用。`,
  };
}

/** 401 统一处理（api() 与 uploadImage() 共用）。 */
function handleUnauthorized(env: ApiEnvId): never {
  const action = unauthorizedAction(env);
  if (action.kind === 'keepSession') throw new Error(action.message);
  setToken(null);
  window.location.assign('/login');
  throw new Error('unauthorized');
}

/** 上传图片（custom 模板背景图）到 R2，返回公开 URL。multipart 不能走 api()
 *  （那里强制 content-type: application/json，会破坏 boundary）。 */
export async function uploadImage(file: File): Promise<string> {
  const token = getToken();
  const body = new FormData();
  body.set('file', file);
  const headers = new Headers();
  if (token) headers.set('authorization', `Bearer ${token}`);
  // 环境读一次:base 与 401 的归属判定必须指向同一个运行时。
  const env = getApiEnv();
  const res = await fetch(`${getApiBase(env)}/api/upload`, { method: 'POST', headers, body });
  if (res.status === 401) handleUnauthorized(env);
  if (!res.ok) {
    const msg = await res
      .json()
      .then((d) => (d as { error?: string }).error)
      .catch(() => undefined);
    throw new Error(msg ?? `upload failed ${res.status}`);
  }
  const { url } = (await res.json()) as { url: string };
  return url;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  // 环境读一次:base 与 401 的归属判定必须指向同一个运行时。
  const env = getApiEnv();
  const res = await fetch(`${getApiBase(env)}${path}`, { ...init, headers });
  if (res.status === 401) handleUnauthorized(env);
  if (!res.ok) throw new Error(await describeError(res));
  return (await res.json()) as T;
}

/** 把服务端错误体(尤其 zod 校验 400)提炼成可读消息,而不是裹死的 `request failed 400`。 */
async function describeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as {
      error?: unknown;
      success?: boolean;
    };
    // @hono/zod-validator 校验失败:{ success:false, error:{ issues:[{path,message}] } }
    const issues = (body.error as { issues?: ZodIssueLike[] })?.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      return `校验失败 (${res.status}): ${formatZodIssues(issues)}`;
    }
    if (typeof body.error === 'string') return `${body.error} (${res.status})`;
  } catch {
    // 非 JSON 响应,落到通用消息。
  }
  return `request failed ${res.status}`;
}

export async function downloadApiFile(path: string, filename: string): Promise<void> {
  const env = getApiEnv();
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(`${getApiBase(env)}${path}`, { headers });
  if (res.status === 401) handleUnauthorized(env);
  if (!res.ok) throw new Error(await describeError(res));
  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
